import { useEffect, useRef, useState } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import gettext from '@sdk/lang/gettext';
import useConfig from '@sdk/config/useConfig';

import { usePricingBreakdown } from '@web/account/hooks/usePricingBreakdown';
import { useTaxCalculations } from '@web/account/hooks/useTaxCalculations';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';

import { Invoice } from './currentBillingPricing.types';
import { mapInvoiceFromAPIToUI } from './currentBillingPricing.utils';
import { Props } from './CurrentBillingPricing';

export const useCurrentBillingPricing = ({
  subscriptionUtils,
  level,
  isYearlyPlan,
  ignoreNextCharge = false,
  onTaxChange,
}: Props) => {
  const { account } = useSlingApi();
  const { billingInfo } = useBillingInfo();
  const { showErrorToast } = useToast();
  const { FEATURE_FLAGS } = useConfig();

  const [upcomingInvoiceSummary, setUpcomingInvoiceSummary] = useState<Invoice>(null);
  const isFetchingInvoice = useRef(false);
  const isPausedSubscription = Boolean(billingInfo.scheduledToResumeDate);
  const [forceInvoiceRefresh, setForceInvoiceRefresh] = useState(false);
  const { taxPercent } = useTaxCalculations(billingInfo?.billingAddress, level, isYearlyPlan);
  const { priceSummary } = usePricingBreakdown({
    isYearlyPlan,
    ignoreNextCharge,
    subscriptionLevel: level,
    subscriptionUtils,
    taxPercent,
  });
  const isSuspendedSubscription =
    !billingInfo.subscriptionStartDate && billingInfo.formerSubscriptionLevel;

  // addresses that are failing on save can be nullified via Admin Panel
  // this flag is used to skip fetching upcoming invoice for those orgs
  // https://toasttab.atlassian.net/browse/SMX-30
  const isBillingAddressNull =
    billingInfo.billingAddress &&
    Object.values(billingInfo.billingAddress).every((v) => v === null);

  // if billing changed due to subscription upgrade / downgrade / cancel we need to refetch upcoming invoice
  useEffect(() => {
    setForceInvoiceRefresh(true);
  }, [billingInfo]);

  useEffect(() => {
    async function fetchUpcomingInvoiceData() {
      try {
        const { data } = await account.getUpcomingInvoiceData();
        setUpcomingInvoiceSummary(mapInvoiceFromAPIToUI(data));
      } catch (e) {
        // if there's no invoice to be fetched, skip the error
        if (e.response.status !== 400) {
          showErrorToast(getErrorForToast(e));
        }

        setUpcomingInvoiceSummary(null);
      } finally {
        isFetchingInvoice.current = false;
      }
    }

    if (
      !ignoreNextCharge &&
      !isPausedSubscription &&
      !isSuspendedSubscription &&
      !isBillingAddressNull &&
      (forceInvoiceRefresh || (!upcomingInvoiceSummary && !isFetchingInvoice.current))
    ) {
      isFetchingInvoice.current = true;
      void fetchUpcomingInvoiceData();
      setForceInvoiceRefresh(false);
    }
  }, [
    forceInvoiceRefresh,
    isPausedSubscription,
    isSuspendedSubscription,
    isBillingAddressNull,
    account,
    showErrorToast,
    upcomingInvoiceSummary,
    ignoreNextCharge,
  ]);

  // if the flag is TRUE we're estimating future cost
  // otherwise we fetch upcoming invoice
  const { prorationAmount, subtotal, discount, tax, total, yearlyDiscount } =
    FEATURE_FLAGS.TAX_CALCULATIONS_ENABLED && !ignoreNextCharge && upcomingInvoiceSummary
      ? upcomingInvoiceSummary
      : {
          tax: priceSummary.taxAmount,
          prorationAmount: 0,
          total: priceSummary.totalGrossPrice,
          subtotal: priceSummary.subtotalPrice,
          discount: priceSummary.discount,
          yearlyDiscount: 0, // in client calculations, subtotal already accounts for yearly discount
        };

  const subtotalBreakdownRows = [
    {
      label: gettext('Subtotal'),
      value: subtotal + yearlyDiscount,
      isHidden: subtotal + yearlyDiscount === total,
    },
    { label: gettext('Coupon'), value: discount, isHidden: !discount },
    { label: gettext('Tax'), value: tax, isHidden: !tax },
  ];

  // in latest version of API proration is already accounted for in subtotal
  // so it needs to be displayed below
  const prorationIndex = FEATURE_FLAGS.TAX_CALCULATIONS_ENABLED ? 0 : 1;
  subtotalBreakdownRows.splice(prorationIndex, 0, {
    label: gettext('Proration'),
    value: prorationAmount,
    isHidden: !prorationAmount,
  });

  useEffect(() => {
    onTaxChange?.(tax);
  }, [tax, onTaxChange]);

  return {
    billingInfo,
    subtotal,
    total,
    subtotalBreakdownRows: subtotalBreakdownRows.filter((r) => !r.isHidden),
    yearlyDiscount,
  };
};
